import React, { useEffect } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CartDrawer from '../components/cart/CartDrawer';
import { siteImages } from '../data/mock';
import { Link } from 'react-router-dom';
import { Eye, Shield, Feather, Sparkles, ArrowRight, Check, Layers } from 'lucide-react';

const MaterialsPage = () => {
  useEffect(() => {
    document.title = 'Premium Materials | Clarity Lens Co';
  }, []);

  const materials = [
    {
      name: 'TR90 Flexible Polymer',
      description: 'An ultra-lightweight, Swiss-engineered thermoplastic material known for its exceptional flexibility and durability. TR90 frames can bend without breaking and return to their original shape.',
      benefits: ['Ultra-lightweight comfort', 'Extremely flexible & durable', 'Hypoallergenic', 'Heat & stress resistant'],
      used: ['Blue Light Glasses', 'Sports Eyewear', 'Kids Eyewear']
    },
    {
      name: 'Premium Acetate',
      description: 'A plant-based material derived from cotton and wood fibers. Acetate offers rich colors, unique patterns, and a luxurious feel that improves with age.',
      benefits: ['Eco-friendly & sustainable', 'Vibrant color options', 'Comfortable fit', 'Adjustable for perfect fit'],
      used: ['Reading Glasses', 'Sunglasses', 'Fashion Frames']
    },
    {
      name: 'Stainless Steel',
      description: 'Medical-grade stainless steel provides exceptional strength with minimal weight. Corrosion-resistant and hypoallergenic, perfect for those with sensitive skin.',
      benefits: ['Incredibly strong & light', 'Corrosion resistant', 'Hypoallergenic', 'Sleek modern aesthetic'],
      used: ['Smart Eyewear', 'Premium Frames']
    },
    {
      name: 'Polycarbonate Lenses',
      description: 'Impact-resistant lens material that provides superior protection. Up to 10x more impact-resistant than glass or standard plastic lenses.',
      benefits: ['10x more impact resistant', 'Built-in UV protection', 'Thin & lightweight', 'Scratch-resistant coating'],
      used: ['All Eyewear', 'Sports Glasses', 'Kids Eyewear']
    }
  ];

  const coatings = [
    { name: 'Anti-Reflective', desc: 'Reduces glare and reflections for clearer vision' },
    { name: 'Blue Light Filter', desc: 'Blocks harmful blue light from digital screens' },
    { name: 'UV400 Protection', desc: '100% protection from UVA and UVB rays' },
    { name: 'Scratch Resistant', desc: 'Durable coating to prevent everyday scratches' },
    { name: 'Oleophobic', desc: 'Repels oils, smudges, and fingerprints' },
    { name: 'Hydrophobic', desc: 'Water-resistant for clear vision in rain' }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <CartDrawer />
      
      <main className="pt-[104px]">
        {/* Hero Banner */}
        <section className="relative h-[240px] overflow-hidden bg-neutral-100">
          <img 
            src={siteImages.productShowcase1}
            alt="Premium Materials"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-neutral-900/80 via-neutral-900/50 to-transparent" />
          <div className="absolute inset-0 flex items-center">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
              <div className="max-w-lg">
                <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-white/10 backdrop-blur-sm rounded-full text-white text-xs font-medium mb-4">
                  <Layers className="w-3.5 h-3.5" />
                  Quality Craftsmanship
                </span>
                <h1 className="font-display text-3xl sm:text-4xl text-white font-semibold tracking-tight mb-2">
                  Premium Materials
                </h1>
                <p className="text-white/80">Crafted with the finest materials for lasting comfort</p>
              </div>
            </div>
          </div>
        </section>

        {/* Intro */}
        <section className="py-16 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="font-display text-2xl text-neutral-900 font-semibold mb-4">
              Quality You Can Feel
            </h2>
            <p className="text-neutral-600 text-lg leading-relaxed">
              At Clarity Lens Co, we believe premium eyewear starts with premium materials. Every frame and lens is crafted using the finest materials available, ensuring durability, comfort, and style that lasts.
            </p>
          </div>
        </section>

        {/* Materials Grid */}
        <section className="py-16 bg-neutral-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-2 gap-6">
              {materials.map((material, i) => (
                <div key={i} className="bg-white rounded-2xl p-8 border border-neutral-200">
                  <h3 className="font-display text-xl text-neutral-900 font-semibold mb-3">
                    {material.name}
                  </h3>
                  <p className="text-neutral-600 mb-5 leading-relaxed">
                    {material.description}
                  </p>
                  <div className="mb-5">
                    <p className="text-sm font-medium text-neutral-500 uppercase tracking-wider mb-3">Benefits</p>
                    <div className="grid grid-cols-2 gap-2">
                      {material.benefits.map((benefit, j) => (
                        <div key={j} className="flex items-center gap-2 text-sm text-neutral-700">
                          <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                          {benefit}
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-neutral-500 uppercase tracking-wider mb-2">Used In</p>
                    <div className="flex flex-wrap gap-2">
                      {material.used.map((item, j) => (
                        <span key={j} className="px-3 py-1 bg-neutral-100 text-neutral-700 text-sm rounded-full">
                          {item}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Lens Coatings */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10">
              <h2 className="font-display text-2xl text-neutral-900 font-semibold mb-2">
                Advanced Lens Coatings
              </h2>
              <p className="text-neutral-600">Protection and clarity built into every lens</p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {coatings.map((coating, i) => (
                <div key={i} className="flex items-start gap-4 p-5 bg-neutral-50 rounded-xl">
                  <div className="w-10 h-10 bg-neutral-900 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Shield className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h4 className="font-medium text-neutral-900 mb-1">{coating.name}</h4>
                    <p className="text-neutral-600 text-sm">{coating.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-neutral-900">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="font-display text-2xl text-white font-semibold mb-4">
              Experience the Difference
            </h2>
            <p className="text-neutral-400 mb-8">
              Feel the quality in every pair of our premium eyewear
            </p>
            <Link 
              to="/shop"
              className="inline-flex items-center gap-2 px-8 py-4 bg-white hover:bg-neutral-100 text-neutral-900 font-medium rounded-full transition-colors"
            >
              Shop Collection
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
};

export default MaterialsPage;
